import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { AccountRoleAndPrivilegesWebComponent } from '../../common/accountRoleAndPrivileges';
import {
  WEB_COMPONENT_ACCOUNT_DETAILS,
  WEB_COMPONENT_ACCOUNT_LOGIN_SECURITY,
  WEB_COMPONENT_ACCOUNT_ROLE_AND_PRIVILEGES,
  WEB_COMPONENT_TARGETS,
} from '../../../webcomponents.constants';
import { translateFn } from '../../../utils/translator.helper';
import { AccountLoginSecurityWebComponent } from '../../common/accountLoginSecurity';
import { TargetsWebComponent } from '../../../targets';
import { AccountDetailsWebComponent } from '../../common/accountDetails';
import { AccountActionContext } from '../../common/types/AccountActionContext';
import { AccountDetails } from '../../common/accountDetails/types';
import { TemplateState } from '../../../types/common/types';
import { CreateAccountEvents, LoggedInUserData } from '../types';
import { CompanyData } from '../../common/types/CompanyData';
import { AccountLoginSecurityData } from '../../common/accountLoginSecurity/types';
import { AccountRoleAndPrivilegesData } from '../../common/accountRoleAndPrivileges/type';
import { SelectedTargets, TargetsViewData } from '../../../targets/type';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { renderSelectTargetsComponent } from '../../helpers/renderSelectTargetsComponent';

export class AddAccountWebView extends BaseWebComponentView {
  public formGroup: FormGroup;
  public dom: {
    $formWrapper: HTMLElement;
    $wrapper: HTMLElement;

    $details: AccountDetailsWebComponent;
    $loginSecurity: AccountLoginSecurityWebComponent;
    $roleAndPrivileges: AccountRoleAndPrivilegesWebComponent;
    $targetsSelection: TargetsWebComponent;

    $createButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  public currentCompany: CompanyData;
  public preselectedTargets: SelectedTargets | undefined;

  getDomElements() {
    this.dom = {
      $formWrapper: this.shadow.querySelector('.form-wrapper'),
      $wrapper: this.shadow.querySelector('.wrapper'),

      $details: this.shadow.querySelector(WEB_COMPONENT_ACCOUNT_DETAILS),
      $loginSecurity: this.shadow.querySelector(WEB_COMPONENT_ACCOUNT_LOGIN_SECURITY),
      $roleAndPrivileges: this.shadow.querySelector(WEB_COMPONENT_ACCOUNT_ROLE_AND_PRIVILEGES),
      $targetsSelection: this.shadow.querySelector(WEB_COMPONENT_TARGETS),

      $createButton: this.shadow.querySelector('#create-button'),
      $cancelButton: this.shadow.querySelector('#cancel-button'),
    };

    this.setLoadingState();

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$createButton.innerText = translateFn('Create');
    this.dom.$cancelButton.innerText = translateFn('Cancel');
  }

  disconnected() {}

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  async buildSuccessView(viewData: LoggedInUserData | null): Promise<void> {
    this.dom.$details.injectDependencies(this.dependencies);
    this.dom.$details.setActionContext(AccountActionContext.Create);
    this.dom.$loginSecurity.injectDependencies(this.dependencies);
    this.dom.$loginSecurity.setActionContext(AccountActionContext.Create);
    this.dom.$roleAndPrivileges.injectDependencies(this.dependencies);
    this.dom.$roleAndPrivileges.setActionContext(AccountActionContext.Create);

    let targetsShouldBeVisible = false;
    if (viewData) {
      this.currentCompany = {
        _id: viewData.companyInfo?._id ?? '',
        name: viewData.companyInfo?.name ?? '',
        specifics: {
          identityProvider: viewData.companyInfo?.specifics?.identityProvider,
          rights: viewData.companyInfo?.specifics?.rights,
        },
      };
      targetsShouldBeVisible = true;

      let accountDetails = {
        adIntegration: viewData.adIntegration,
        companyInfo: viewData.companyInfo,
      } as AccountDetails;

      let accountLoginSecurity = {
        companyInfo: viewData.companyInfo,
      } as AccountLoginSecurityData;

      let accountRoleAndPrivileges = {
        companyInfo: viewData.companyInfo,
      } as AccountRoleAndPrivilegesData;

      this.preselectedTargets = viewData.serviceTargets;

      this.dom.$details.setViewData(accountDetails);
      this.dom.$loginSecurity.setViewData(accountLoginSecurity);
      this.dom.$roleAndPrivileges.setViewData(accountRoleAndPrivileges);
    }

    await this.dom.$details.start();
    await this.dom.$loginSecurity.start();
    await this.dom.$roleAndPrivileges.start();

    // start targets component if it should be visible
    if (targetsShouldBeVisible && this.dom.$roleAndPrivileges.mustSelectTargets()) {
      let targetsViewData = {
        companyId: this.currentCompany._id,
        availableServices: this.dependencies.getAvailableServices(),
      } as TargetsViewData;

      if (this.preselectedTargets) {
        targetsViewData.preSelectedTargets = this.preselectedTargets;
      }
      await renderSelectTargetsComponent(this.dom.$targetsSelection, targetsViewData, this.dependencies);
      toggleVisibility(this.dom.$targetsSelection, true);
    }

    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(CreateAccountEvents.InitCreateAccountForm, {
        bubbles: true,
        composed: true,
      })
    );
    this.removeLoadingState();
  }

  public async changeFormFieldsVisibilityDependingOnSelectedCompany(): Promise<void> {
    this.dom.$loginSecurity.updateSettingsDependingOnCompany(this.currentCompany);
    await this.dom.$roleAndPrivileges.updateSettingsDependingOnCompany(this.currentCompany);
  }
}
